import { CircularProgress, Grid, Tab, Tabs } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useTitle } from 'hooks'
import backend from 'utils/backend'
import history from 'utils/history'
import { trackPageView } from 'utils/mixpanel'

import { redirectTo } from 'store/actions/user'
import { selectHasExecutePermissions, selectUser } from 'store/selectors/user'

import UserChangeLogin from './components/UserChangeLogin'
import UserList from './components/UserList'
import UserManagePlan from './components/UserManagePlan'
import UserManageTokens from './components/UserManageTokens'

const TABS = [UserManagePlan, UserList, UserChangeLogin, UserManageTokens]

const TabPanel = ({ children, value, index }) => {
  return (
    <div role="tabpanel" className="tab-panel" hidden={value !== index}>
      {children}
    </div>
  )
}

const UserProfilePage = () => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const dispatch = useDispatch()

  const { tab } = useParams()

  const { organization, firstName, lastName } = useSelector(selectUser)
  const hasExecutePermissions = useSelector(selectHasExecutePermissions)

  const [activeTab, setActiveTab] = useState(-1)

  const { isLoading: patScopesLoading, data: patScopes } = useQuery({
    queryKey: ['patScopes'],
    queryFn: () => backend.get('api/token/pat/scopes/').then(res => res.data.results)
  })

  const isLoading = !organization || patScopesLoading
  const enabledTabs = useMemo(
    () => (isLoading ? [] : TABS.filter(tab => tab.isEnabled({ patScopes }))),
    [isLoading]
  )

  const gotoTab = tab => {
    history.push(`/user-profile/${tab}`)
  }

  const onTabChange = (e, newValue) => {
    gotoTab(enabledTabs[newValue].tabPath)
  }

  useTitle(t => t.user.profile.myAccount)

  useEffect(() => {
    trackPageView()
  }, [])

  useEffect(() => {
    if (!hasExecutePermissions) {
      dispatch(redirectTo('/search', { replace: true }))
    }
  }, [hasExecutePermissions])

  useEffect(() => {
    if (isLoading) {
      return
    }
    const tabIndex = enabledTabs.findIndex(tabComponent => tabComponent.tabPath === tab)
    if (tabIndex === -1) {
      // assumes there's always at least one enabled tab
      gotoTab(enabledTabs[0].tabPath)
    } else {
      setActiveTab(tabIndex)
    }
  }, [isLoading, tab])

  if (activeTab < 0) {
    return (
      <Grid container className="main user-profile" justifyContent="center">
        <CircularProgress />
      </Grid>
    )
  }

  return (
    <div className="main user-profile">
      <h1>{t.user.profile.myAccount}</h1>
      <Grid container className="profile-container">
        <div className="tab-container">
          <p className="greeting">
            {t.user.profile.greeting},&nbsp;
            <span className="username">{firstName || lastName}</span>
          </p>
          <Tabs value={activeTab} onChange={onTabChange} orientation="vertical">
            {enabledTabs.map(tabComponent => (
              <Tab key={tabComponent.tabPath} label={tabComponent.getTabName(t)} />
            ))}
          </Tabs>
        </div>
        {enabledTabs.map((TabComponent, index) => (
          <TabPanel key={TabComponent.tabPath} value={activeTab} index={index}>
            <TabComponent userData={{ patScopes }} />
          </TabPanel>
        ))}
      </Grid>
    </div>
  )
}

export default UserProfilePage
